import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { StudiesAPI, DFoldersAPI, DownloadJobsAPI } from '../../api/client';
import Loading from '../../components/Common/Loading';

const StudyDetailPage = () => {
  const { id } = useParams();
  const [study, setStudy] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  useEffect(() => {
    loadStudyDetails();
  }, [id]);

  const loadStudyDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load study details and d-folders in parallel
      const [studyData, foldersData] = await Promise.all([
        StudiesAPI.retrieve(id),
        StudiesAPI.dFolders(id)
      ]);

      setStudy(studyData);
      // Handle both paginated and non-paginated responses
      const foldersArray = Array.isArray(foldersData) ? foldersData : (foldersData.results || []);
      setFolders(foldersArray);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load study details');
      console.error('Error loading study details:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFolderDownloadable = (folder) => {
    return folder.gcs_path != null && folder.gcs_path !== '';
  };

  const toggleFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder || !isFolderDownloadable(folder)) {
      return; // Don't allow toggling non-downloadable folders
    }

    const newSelected = new Set(selectedFolders);
    if (newSelected.has(folderId)) {
      newSelected.delete(folderId);
    } else {
      newSelected.add(folderId);
    }
    setSelectedFolders(newSelected);
  };

  const toggleAll = () => {
    const downloadableFolders = filteredFolders.filter(isFolderDownloadable);
    const downloadableFolderIds = downloadableFolders.map(f => f.id);
    const allDownloadableSelected = downloadableFolderIds.every(id => selectedFolders.has(id));

    if (allDownloadableSelected && downloadableFolders.length > 0) {
      // Deselect all
      setSelectedFolders(new Set());
    } else {
      // Select all downloadable folders
      setSelectedFolders(new Set(downloadableFolderIds));
    }
  };

  const handleDownload = async () => {
    if (selectedFolders.size === 0) {
      alert('Please select at least one d-folder to download');
      return;
    }

    // Filter out any non-downloadable folders (defensive programming)
    const downloadableFolderIds = Array.from(selectedFolders).filter(folderId => {
      const folder = folders.find(f => f.id === folderId);
      return folder && isFolderDownloadable(folder);
    });

    if (downloadableFolderIds.length === 0) {
      alert('None of the selected folders can be downloaded (missing GCS path)');
      return;
    }

    try {
      setDownloading(true);
      const job = await DownloadJobsAPI.create(downloadableFolderIds);
      alert(`Download job created! Job ID: ${job.id}\nYou can monitor its progress in the Downloads page.`);
      setSelectedFolders(new Set());
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create download job';
      alert(`Error: ${errorMsg}`);
      console.error('Error creating download job:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const response = await DFoldersAPI.exportExcel({
        study: id,
        export_format: exportFormat
      });

      // Determine content type based on format
      const contentType = exportFormat === 'csv'
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      // Create blob and download
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `study_${study?.name}_export.${exportFormat}`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export file');
      console.error('Error exporting:', err);
    } finally {
      setExporting(false);
    }
  };

  const filteredFolders = folders.filter(folder =>
    folder.gcs_path?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (folder.metadata && JSON.stringify(folder.metadata).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading message="Loading study details..." size="page" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading study</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
        <Link to="/studies" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
          ← Back to Studies
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Link to="/studies" className="text-blue-600 hover:text-blue-800 text-sm">
          ← Back to Studies
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{study?.name}</h1>
            {study?.description && (
              <p className="mt-2 text-gray-600">{study.description}</p>
            )}
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span>{folders.length} d-folders</span>
              </div>
              {study?.is_active ? (
                <span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                  Active
                </span>
              ) : (
                <span className="px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:w-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search d-folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-gray-700"
            disabled={exporting || folders.length === 0}
          >
            <option value="csv">CSV (.csv)</option>
            <option value="xlsx">Excel (.xlsx)</option>
          </select>
          <button
            onClick={handleExportExcel}
            disabled={exporting || folders.length === 0}
            className="flex-1 sm:flex-none px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {exporting ? 'Exporting...' : 'Export Metadata'}
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading || selectedFolders.size === 0}
            className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {downloading ? 'Creating Job...' : `Download (${selectedFolders.size})`}
          </button>
        </div>
      </div>

      {/* D-Folders Table */}
      {filteredFolders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No d-folders found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search.' : 'This study has no d-folders yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        filteredFolders.filter(isFolderDownloadable).length > 0 &&
                        filteredFolders.filter(isFolderDownloadable).every(f => selectedFolders.has(f.id))
                      }
                      onChange={toggleAll}
                      disabled={filteredFolders.filter(isFolderDownloadable).length === 0}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Folder Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Files
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredFolders.map((folder) => {
                  const isDownloadable = isFolderDownloadable(folder);
                  return (
                    <tr key={folder.id} className={`hover:bg-gray-50 ${!isDownloadable ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedFolders.has(folder.id)}
                          onChange={() => toggleFolder(folder.id)}
                          disabled={!isDownloadable}
                          title={!isDownloadable ? 'This folder has no GCS path and cannot be downloaded' : ''}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-mono break-all">
                          {folder.id}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-mono break-all">
                          {folder.folder_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {folder.file_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-40">
                        <div className="flex flex-col gap-1">
                          {!isDownloadable && (
                            <span className="px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full" title="No GCS path available">
                              Not Downloadable
                            </span>
                          )}
                          {isDownloadable && folder.has_metadata && (
                            <span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                              Metadata
                            </span>
                          )}
                          {isDownloadable && folder.has_biomarker && (
                            <span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                              Has Biomarker
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
            Showing {filteredFolders.length} of {folders.length} d-folders
            <span className="ml-4 text-gray-600">
              ({filteredFolders.filter(isFolderDownloadable).length} downloadable)
            </span>
            {selectedFolders.size > 0 && (
              <span className="ml-4 text-blue-600">
                {selectedFolders.size} selected
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyDetailPage;
